import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { useOutreachRequest } from '@round/creatorbase-helpers/api-hooks';
import useOutreachRequestId from './hooks/useOutreachRequestId';
import useSyncSelectedClient from './hooks/useSyncSelectedClient';
import { BACK_TO_LIST_LABEL, OUTREACH_LIST_PATH } from './helpers';
import { OutreachRequestInfo } from './components/OutreachRequestInfo/OutreachRequestInfo';
import { OutreachRequestConfiguration } from './components/OutreachRequestConfiguration/OutreachRequestConfiguration';
import { OutreachRequestResults } from './components/OutreachRequestResults/OutreachRequestResults';
import styles from './OutreachRequestDetails.module.css';

// How much of the setup section may still show beneath the headline before its summary takes over,
// and how much has to come back before it hands over again — a gap wide enough that the two don't
// trade places on a single stray scroll.
const COMPACT_ENTER_OFFSET_PX = 8;
const COMPACT_EXIT_OFFSET_PX = 24;

/** Holds the headline's height, which is what the results are pinned beneath. */
const HEADER_HEIGHT_PROPERTY = '--outreach-details-header-height';

const LOAD_ERROR_MESSAGE = "We couldn't open this outreach request.";

const OutreachRequestDetails = () => {
    const requestId = useOutreachRequestId();
    const { request, isLoading, isError, isNotFound } = useOutreachRequest(requestId);

    useSyncSelectedClient(request?.client_id);

    // A request that isn't there and a request that wouldn't load leave the page in the same state:
    // nothing to show, and nothing worth pressing — a reload is what recovers either.
    const hasFailedToLoad = isNotFound || isError;

    const pageRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const configurationRef = useRef<HTMLDivElement>(null);
    const [isConfigurationCompact, setIsConfigurationCompact] = useState(false);

    // The headline stays put, so what it takes is measured rather than guessed: the name can wrap and
    // the meta row can run onto a second line.
    useLayoutEffect(() => {
        const page = pageRef.current;
        const header = headerRef.current;

        if (!page || !header) {
            return;
        }

        const update = () => page.style.setProperty(HEADER_HEIGHT_PROPERTY, `${header.offsetHeight}px`);

        update();

        const observer = new ResizeObserver(update);
        observer.observe(header);

        return () => observer.disconnect();
    }, []);

    // Driven by the page's own scroll: the headline and the results are both pinned, so the setup
    // section is what the page scrolls through, and its summary takes over as it goes.
    useEffect(() => {
        const page = pageRef.current;

        if (!page) {
            return;
        }

        const handleScroll = () => {
            const header = headerRef.current;
            const configuration = configurationRef.current;

            if (!header || !configuration) {
                return;
            }

            const offset = configuration.getBoundingClientRect().bottom - header.getBoundingClientRect().bottom;

            setIsConfigurationCompact((isCompact) =>
                isCompact ? offset <= COMPACT_EXIT_OFFSET_PX : offset <= COMPACT_ENTER_OFFSET_PX
            );
        };

        handleScroll();
        page.addEventListener('scroll', handleScroll);

        return () => page.removeEventListener('scroll', handleScroll);
    }, [request]);

    return (
        <div ref={pageRef} className={cn(styles.container, 'twp')}>
            <header ref={headerRef} className={styles.infoSection}>
                {hasFailedToLoad ? (
                    <div className={styles.loadError}>
                        <p className={styles.loadErrorMessage}>{LOAD_ERROR_MESSAGE}</p>
                        <Link className={styles.loadErrorLink} to={OUTREACH_LIST_PATH}>
                            {BACK_TO_LIST_LABEL}
                        </Link>
                    </div>
                ) : (
                    <OutreachRequestInfo request={request} isLoading={isLoading} />
                )}
            </header>

            {!!request && !isLoading && (
                <>
                    <div ref={configurationRef} className={styles.configurationSection}>
                        <OutreachRequestConfiguration request={request} />
                    </div>

                    <div className={styles.resultsPane}>
                        {isConfigurationCompact && (
                            <div className={styles.compactBar}>
                                <OutreachRequestConfiguration request={request} isCompact />
                            </div>
                        )}

                        <div className={styles.resultsSection}>
                            <OutreachRequestResults request={request} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default OutreachRequestDetails;
